import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, Dumbbell, ListOrdered, Sparkles, Camera } from "lucide-react";
import { motion } from "framer-motion";
import { apiClient } from "../../services/api";
import VideoPlayer from "../../components/workout/VideoPlayer";
import FloatingCTA from "../../components/layout/FloatingCTA";
import PersonalAITrainer from "../../components/personal-ai-trainer/PersonalAITrainer";
import { useAuthStore } from "../../store/authStore";
import { toast } from "sonner";

const DIFF_COLOR = {
  beginner: "var(--accent-green)",
  intermediate: "#FFB257",
  advanced: "#FF5C5C",
};

export default function ExerciseDetail() {
  const { exerciseId } = useParams();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const [ex, setEx] = useState(null);
  const [logging, setLogging] = useState(false);
  const [trainerOpen, setTrainerOpen] = useState(false);

  useEffect(() => {
    apiClient
      .get(`/exercises/${exerciseId}`)
      .then((r) => setEx(r.data))
      .catch(() => navigate("/workouts/gym", { replace: true }));
  }, [exerciseId]);

  if (!ex) {
    return (
      <div className="min-h-screen grid place-items-center bg-canvas">
        <div className="text-ink-muted">Loading…</div>
      </div>
    );
  }

  const dotColor = DIFF_COLOR[ex.difficulty] || "var(--text-muted)";

  const logQuickSet = async () => {
    if (!user) {
      navigate("/login");
      return;
    }
    setLogging(true);
    try {
      await apiClient.post("/workout/sessions", {
        plan_id: null,
        exercises: [
          { exercise_id: ex.exercise_id, name: ex.name, sets: 3, reps: "10" },
        ],
        duration_minutes: 5,
        calories_burned: 35,
      });
      toast.success("Quick set logged · +50 FitPoints");
      setTimeout(() => navigate("/home"), 1100);
    } catch (e) {
      toast.error("Could not log set");
    } finally {
      setLogging(false);
    }
  };

  return (
    <div className="min-h-screen bg-canvas pb-32" data-testid="exercise-detail">
      {/* Hero — compact so the demo video below stays in view */}
      <div className="relative h-[150px] sm:h-[170px]">
        <img
          src={ex.hero_url || ex.thumbnail_url}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: "center 30%" }}
        />
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.78) 100%)" }}
        />
        <button
          onClick={() => navigate(-1)}
          aria-label="Back"
          data-testid="exercise-back"
          className="absolute top-3 left-3 w-9 h-9 grid place-items-center rounded-full bg-white/85 backdrop-blur"
        >
          <ChevronLeft size={18} />
        </button>
        <div className="absolute bottom-3 left-4 right-4 text-white">
          <div className="text-[10px] uppercase tracking-wider opacity-85">{ex.muscle_group}</div>
          <h1 className="font-display text-[22px] sm:text-[24px] leading-[1.1] mt-0.5">{ex.name}</h1>
          <div className="flex items-center gap-1.5 mt-1.5 text-[11px]">
            <span className="px-2 py-0.5 rounded-pill bg-white/15 backdrop-blur capitalize">{ex.equipment}</span>
            <span className="flex items-center gap-1 px-2 py-0.5 rounded-pill bg-white/15 backdrop-blur capitalize">
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: dotColor }} />
              {ex.difficulty}
            </span>
          </div>
        </div>
      </div>

      <main className="max-w-2xl mx-auto px-4 pt-4 space-y-5">
        {/* Video — strict 3-tier fallback */}
        <motion.section
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          data-testid="exercise-video-section"
        >
          <VideoPlayer
            s3Url={ex.s3_video_url || ex.video_url}
            s3MobileUrl={ex.s3_video_mobile_url}
            ytId={ex.youtube_id}
            thumbnail={ex.thumbnail_url}
            exerciseName={ex.name}
          />
          <button
            type="button"
            onClick={() => setTrainerOpen(true)}
            data-testid="start-ai-trainer"
            className="mt-2.5 w-full h-11 rounded-[12px] flex items-center justify-center gap-2 border border-line2 bg-white text-[14px] font-semibold text-ink"
          >
            <Camera size={16} />
            Start AI Trainer
          </button>
        </motion.section>

        {/* Form cues */}
        {ex.form_cues?.length > 0 && (
          <section
            className="bg-white rounded-[16px] p-5 border border-line2"
            style={{ boxShadow: "var(--shadow-card)" }}
            data-testid="form-cues"
          >
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 grid place-items-center rounded-full" style={{ background: "var(--accent-blue-dim)" }}>
                <Sparkles size={14} style={{ color: "var(--accent-blue)" }} />
              </div>
              <h2 className="font-display text-[20px]">Form cues</h2>
            </div>
            <ul className="mt-3 space-y-2">
              {ex.form_cues.map((c, i) => (
                <li key={i} className="flex items-start gap-2 text-[14.5px] text-ink leading-relaxed">
                  <span
                    className="mt-2 w-1.5 h-1.5 rounded-full shrink-0"
                    style={{ background: "var(--accent-blue)" }}
                  />
                  {c}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Suggested set/rep prescription */}
        <section
          className="bg-white rounded-[16px] p-5 border border-line2"
          style={{ boxShadow: "var(--shadow-card)" }}
          data-testid="suggested-prescription"
        >
          <div className="flex items-center gap-2 mb-3">
            <ListOrdered size={18} className="text-ink-soft" />
            <h2 className="font-display text-[20px]">Suggested today</h2>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {prescriptionFor(ex).map((p) => (
              <div key={p.label} className="rounded-[12px] py-3 px-2 text-center bg-canvas2">
                <div className="font-display text-[24px] leading-none">{p.value}</div>
                <div className="text-[11px] uppercase tracking-wider text-ink-muted mt-1.5">{p.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Tags */}
        <section>
          <h2 className="font-display text-[18px] mb-2">Targets</h2>
          <div className="flex flex-wrap gap-1.5">
            <span className="text-[12px] px-3 h-7 rounded-pill bg-white border border-line capitalize grid place-items-center" style={{ borderRadius: 100 }}>
              {ex.muscle_group}
            </span>
            <span className="text-[12px] px-3 h-7 rounded-pill bg-white border border-line capitalize grid place-items-center" style={{ borderRadius: 100 }}>
              {ex.equipment}
            </span>
            {ex.category && (
              <span className="text-[12px] px-3 h-7 rounded-pill bg-white border border-line capitalize grid place-items-center" style={{ borderRadius: 100 }}>
                {ex.category}
              </span>
            )}
            {ex.impact_level && (
              <span className="text-[12px] px-3 h-7 rounded-pill bg-white border border-line capitalize grid place-items-center" style={{ borderRadius: 100 }}>
                {ex.impact_level} impact
              </span>
            )}
          </div>
        </section>
      </main>

      <FloatingCTA
        label={logging ? "Logging…" : "Log a quick set"}
        onClick={logQuickSet}
        disabled={logging}
        icon={Dumbbell}
        testid="log-quick-set"
        hint="3 sets × 10 reps · +50 FitPoints"
      />

      {trainerOpen && (
        <PersonalAITrainer
          exerciseName={ex.name}
          active
          fullScreen
          onClose={() => setTrainerOpen(false)}
        />
      )}
    </div>
  );
}

function prescriptionFor(ex) {
  const lvl = ex.difficulty;
  const isCardio = ex.muscle_group === "cardio" || ex.category === "fat_burn";
  if (isCardio) {
    return [
      { label: "Sets", value: lvl === "beginner" ? 2 : lvl === "advanced" ? 4 : 3 },
      { label: "Time", value: "45s" },
      { label: "Rest", value: "30s" },
    ];
  }
  const reps = lvl === "advanced" ? "6-8" : lvl === "beginner" ? "12-15" : "8-10";
  const sets = lvl === "advanced" ? 5 : lvl === "beginner" ? 3 : 4;
  return [
    { label: "Sets", value: sets },
    { label: "Reps", value: reps },
    { label: "Rest", value: "75s" },
  ];
}
